import type { OperationalAwardTypeCode, PayFigures, PaysAs } from '@myrewards/contracts';
import { isApiError } from '../api';
import { problemWords, TYPE_SHORT } from '../ops/labels';
import { formatMoney, plural } from '../ui';

// Words the closing screens share: Payroll (P1), the earning statement check (SA6) and the
// Shareworks worklist (SA5). Plain stages only, and every tax figure before Payroll's is an
// estimate.

/** How a line is paid, in the words the election used. */
export const PAYS_AS: Record<PaysAs, { label: string; detail: string }> = {
  a: { label: 'PAA · choice (a)', detail: 'All cash, tax withheld' },
  b: { label: 'PAA · choice (b)', detail: 'Most stock, the employee pays the tax' },
  c: { label: 'PAA · choice (c)', detail: 'Most stock, tax withheld' },
  d: { label: 'PAA · choice (d)', detail: 'A custom split' },
  closed_as_cash: { label: 'PAA · closed as cash', detail: 'Closed as cash after the deadline' },
  cash: { label: 'Cash', detail: 'Cash only' },
};

/** Choices that buy shares: the statement's numbers need the award's FMV to add up. */
export function buysShares(payAs: PaysAs): boolean {
  return payAs === 'b' || payAs === 'c' || payAs === 'd';
}

/** "Cash", or "PAA · choice (c)" with its detail. The type says which award a cash line is on. */
export function paysAsWords(
  payAs: PaysAs,
  type: OperationalAwardTypeCode,
): { label: string; detail: string } {
  if (payAs !== 'cash') return PAYS_AS[payAs];
  return { label: 'Cash', detail: TYPE_SHORT[type] };
}

/** "$1,068.75", "$31.55 + 91 shares", "91 shares": what reaches the employee. */
export function netWords(figures: Pick<PayFigures, 'cashCents' | 'shares'>): string {
  if (figures.shares === 0) return formatMoney(figures.cashCents);
  const shares = plural(figures.shares, 'share', 'shares');
  return figures.cashCents === 0 ? shares : `${formatMoney(figures.cashCents)} + ${shares}`;
}

/** "2 hours", "90 minutes", "1 hour 30 minutes": the claim timeout in words. */
export function minutesWords(minutes: number): string {
  if (minutes < 60) return plural(minutes, 'minute', 'minutes');
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  const h = plural(hours, 'hour', 'hours');
  return rest === 0 ? h : `${h} ${plural(rest, 'minute', 'minutes')}`;
}

/** Plain words for the closing screens' problem codes; anything else as the operations screens say it. */
export function closeProblemWords(error: unknown): string {
  if (!isApiError(error)) return problemWords(error);
  switch (error.code) {
    case 'final_tax_above_cash':
      return 'The final tax can’t be more than the cash.';
    case 'numbers_differ':
      return 'The share count changed, so this can’t simply be confirmed. Record Payroll’s numbers with a reason instead.';
    case 'figures_dont_add_up':
      return 'The cash paid, the shares at FMV and the tax withheld need to add up to the award’s cash.';
    case 'already_claimed':
      return `${error.message} Their name shows on the row.`;
    case 'earning_statement_required':
      return 'Payroll attaches the earning statement first.';
    case 'not_paid':
      return 'Payroll hasn’t paid this yet.';
    case 'shareworks_not_claimed':
      return 'Claim these rows before checking them off. Nothing was checked off.';
    case 'conflict':
      return 'Someone changed this while you were working. We’ve loaded the latest; check it and try again.';
    default:
      return problemWords(error);
  }
}

/** The rows a `shareworks_not_claimed` answer names (its issues carry the line ids). */
export function notClaimedLines(error: unknown): string[] {
  if (!isApiError(error) || error.code !== 'shareworks_not_claimed') return [];
  return (error.problem?.issues ?? []).map((i) => i.path);
}
